import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiPackage,
  FiSearch,
  FiTag,
  FiLayers,
  FiX,
  FiChevronRight,
  FiRefreshCw,
} from "react-icons/fi";
import axios from "../../Utils/axiosInstance";
import { searchProductVarieties } from "../../Utils/ProductSearchUtils";

const AllSpecialVarieties = () => {
  const [varieties, setVarieties] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchSpecialVarieties = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      // 1. Fetch special categories
      const { data: categoryRes } = await axios.get("/category/get-all", {
        params: { special: true },
      });

      const specialCategories = categoryRes?.data || [];
      setCategories(specialCategories);

      if (specialCategories.length === 0) {
        setVarieties([]);
        return;
      }

      // 2. Fetch active product varieties for each special category
      const varietyRequests = specialCategories.map((category) =>
        axios.get("/product-variety/get-all", {
          params: {
            categoryId: category._id,
            isActive: true,
            populate: "true",
          },
        })
      );

      const varietyResponses = await Promise.all(varietyRequests);

      // 3. Flatten and tag each variety with its parent category
      const allVarieties = varietyResponses.flatMap((res, index) => {
        const list = res?.data?.data || [];
        return list.map((variety) => ({
          ...variety,
          parentCategory: specialCategories[index],
        }));
      });

      // Deduplicate by variety _id if any overlaps occur
      const uniqueMap = new Map();
      allVarieties.forEach((v) => {
        if (v._id && !uniqueMap.has(v._id)) {
          uniqueMap.set(v._id, v);
        }
      });

      setVarieties(Array.from(uniqueMap.values()));
    } catch (err) {
      console.error("Failed to load special category varieties:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSpecialVarieties();
  }, [fetchSpecialVarieties]);

  // Filter varieties by selected category
  const categoryFilteredVarieties = useMemo(() => {
    if (selectedCategoryId === "all") {
      return varieties;
    }
    return varieties.filter((variety) => {
      const parentCatId =
        variety.parentCategory?._id ||
        variety.productId?.subCategoryId?.categoryId?._id;
      return parentCatId === selectedCategoryId;
    });
  }, [varieties, selectedCategoryId]);

  // Apply search filtering on top of category filtering
  const displayVarieties = useMemo(() => {
    if (!query.trim()) {
      return categoryFilteredVarieties;
    }
    return searchProductVarieties(query.trim(), categoryFilteredVarieties);
  }, [query, categoryFilteredVarieties]);

  // Build target link to /products with full selection parameters
  const getVarietyHref = (variety) => {
    const params = new URLSearchParams();

    const categoryId =
      variety?.productId?.subCategoryId?.categoryId?._id ||
      variety?.productId?.subCategoryId?.categoryId ||
      variety?.parentCategory?._id ||
      "";

    const subCategoryId =
      variety?.productId?.subCategoryId?._id || variety?.subCategoryId || "";

    const productId = variety?.productId?._id || variety?.productId || "";

    if (categoryId) params.set("category", categoryId);
    if (subCategoryId) params.set("subCategory", subCategoryId);
    if (productId) params.set("product", productId);
    if (variety?._id) params.set("variety", variety._id);

    return `/products${params.toString() ? `?${params.toString()}` : ""}`;
  };

  return (
    <div className="min-h-screen bg-[#FFFCFC]">
      <div className="2xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Breadcrumb Navigation */}
        <nav className="mb-6 flex items-center gap-2 text-xs font-medium text-gray-500">
          <Link to="/" className="hover:text-primaryColor transition-colors">
            Home
          </Link>
          <FiChevronRight className="text-gray-400" />
          <span className="text-primaryColor font-semibold">
            Plant Extracts
          </span>
        </nav>

        {/* Page Header */}
        <div className="mb-8 md:mb-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#3D0F1B] to-subPrimaryColor py-1">
                Plant Extracts
              </h1>
              <p className="mt-2 max-w-2xl text-xs sm:text-sm text-gray-500 leading-relaxed">
                Explore our curated range of high-quality plant extracts product varieties.
              </p>
            </div>

            {!loading && !error && (
              <div className="inline-flex items-center gap-2 self-start sm:self-auto rounded-full bg-primaryColor/10 px-4 py-1.5 text-xs font-semibold text-primaryColor">
                <span>Total:</span>
                <span className="font-bold">{displayVarieties.length}</span>
                <span>varieties</span>
              </div>
            )}
          </div>
        </div>

        {/* Search & Category Filter Section */}
        <div className="mb-8 space-y-4">
          <div className="relative max-w-3xl">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search special category varieties by name, grade, or product..."
              className="w-full rounded-full border border-gray-200 bg-white py-3.5 pl-12 pr-10 text-sm text-gray-800 shadow-sm outline-none transition focus:border-primaryColor/70 focus:ring-2 focus:ring-primaryColor/10"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:text-gray-600 transition"
                aria-label="Clear search"
              >
                <FiX className="text-base" />
              </button>
            )}
          </div>
        </div>

        {/* ERROR STATE */}
        {error && (
          <div className="rounded-3xl border border-dashed border-red-200 bg-red-50/50 p-10 text-center max-w-xl mx-auto">
            <div className="text-4xl mb-3">📡</div>
            <p className="text-base font-semibold text-gray-800">
              Unable to load special varieties right now
            </p>
            <p className="mt-2 text-xs sm:text-sm text-gray-500">
              Please check your network connection and try again.
            </p>
            <button
              onClick={fetchSpecialVarieties}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-primaryColor px-5 py-2.5 text-xs font-semibold text-white transition hover:bg-primaryBtnHoverColor active:scale-95 shadow-sm"
            >
              <FiRefreshCw className="text-sm" />
              Retry
            </button>
          </div>
        )}

        {/* LOADING SKELETON */}
        {!error && loading && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="rounded-[1.5rem] border border-gray-200 bg-white p-3 shadow-sm animate-pulse"
              >
                <div className="h-44 rounded-[1.25rem] bg-gray-200" />
                <div className="p-3 space-y-3">
                  <div className="flex justify-between gap-2">
                    <div className="h-4 w-20 rounded-full bg-gray-200" />
                    <div className="h-4 w-24 rounded bg-gray-200" />
                  </div>
                  <div className="h-5 w-3/4 rounded bg-gray-200" />
                  <div className="h-10 w-full rounded-lg bg-gray-100" />
                  <div className="h-10 w-full rounded-2xl bg-gray-100" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* EMPTY STATE */}
        {!error && !loading && displayVarieties.length === 0 && (
          <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-10 text-center max-w-xl mx-auto">
            <div className="text-5xl mb-4">🌿</div>
            <p className="text-base font-semibold text-gray-800">
              {query.trim()
                ? "No special varieties match your search"
                : "No special category product varieties found"}
            </p>
            <p className="mt-2 text-xs sm:text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
              {query.trim()
                ? "Try checking your spelling or adjusting search keywords."
                : "Special category product varieties will appear here once available."}
            </p>
            {query.trim() && (
              <button
                onClick={() => setQuery("")}
                className="mt-5 inline-flex items-center rounded-full bg-primaryColor px-5 py-2 text-xs font-semibold text-white transition hover:bg-primaryBtnHoverColor"
              >
                Clear Search
              </button>
            )}
          </div>
        )}

        {/* VARIETIES GRID */}
        {!error && !loading && displayVarieties.length > 0 && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {displayVarieties.map((variety) => {
              const image = variety?.img;
              const productName =
                variety?.productId?.name ||
                variety?.parentCategory?.name ||
                "Product";
              const categoryName =
                variety?.parentCategory?.name ||
                variety?.productId?.subCategoryId?.categoryId?.name ||
                variety?.productId?.subCategoryId?.name ||
                "Special Category";

              return (
                <Link
                  key={variety._id}
                  to={getVarietyHref(variety)}
                  className="group mx-auto flex w-full max-w-[21rem] flex-col rounded-[1.5rem] border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-primaryColor/30 overflow-hidden"
                >
                  {/* Image Container */}
                  <div className="relative overflow-hidden rounded-t-[1.5rem] bg-gradient-to-br from-[#FFF4F6] via-white to-[#FDEFF2]">
                    {image ? (
                      <img
                        src={image}
                        alt={variety?.varietyName || productName}
                        className="h-44 sm:h-48 w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-44 sm:h-48 w-full items-center justify-center bg-gradient-to-br from-[#FFEDEE] to-[#F9F0F2]">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/80 shadow-sm text-primaryColor">
                          <FiPackage className="text-3xl" />
                        </div>
                      </div>
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />

                    {/* Floating Category Badge */}
                    <div className="absolute left-3 top-3 rounded-full bg-white/95 backdrop-blur-sm px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primaryColor shadow-sm">
                      {categoryName}
                    </div>
                  </div>

                  {/* Card Details */}
                  <div className="flex flex-1 flex-col p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-primaryColor/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-primaryColor">
                        <FiTag className="text-[11px]" />
                        {variety?.grade || "Premium"}
                      </span>
                      <span className="text-[11px] font-medium text-gray-500 truncate max-w-[55%]">
                        {productName}
                      </span>
                    </div>

                    <h3 className="mt-3 text-base font-semibold text-[#3D0F1B] group-hover:text-primaryColor transition-colors flex items-center justify-between gap-2">
                      <span className="line-clamp-1">{variety?.varietyName || "Product Variety"}</span>
                      <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                        MOQ {(variety?.slabAdjustedMOQ ?? variety?.minimum_MOQ) || 0} {variety?.standardizedUNIT || "unit"}
                      </span>
                    </h3>

                    <div className="mt-4 flex items-center justify-between rounded-2xl border border-gray-100 bg-[#FFF9FA] px-3.5 py-2 transition-colors group-hover:bg-primaryColor/10">
                      <span className="flex items-center gap-2 text-xs font-semibold text-gray-700 group-hover:text-primaryColor">
                        <FiLayers className="text-primaryColor text-sm" />
                        Explore variety
                      </span>
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primaryColor text-white transition-transform duration-300 group-hover:translate-x-1 shadow-sm">
                        <FiArrowRight className="text-xs" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllSpecialVarieties;
